import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Dimensions,
  FlatList,
  Image,
  Modal,
  Pressable,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import AmbientGlow from '../components/AmbientGlow';
import LoadingSpinner from '../components/LoadingSpinner';
import { Colors, Radius, Typography } from '../theme';
import type { RootStackParamList, TonyteActivity } from '../types';
import { resolveActivityImage } from '../utils/activityImages';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import {
  buildOrganizerSummaries,
  filterOrganizerEvents,
  getCollabLabel,
  shareOrganizerProfile,
  splitVenueEvents,
  useOrganizerFollow,
  useVenuePostInteractions,
  useVenuePostStats,
  useVenueVibes,
} from '../hooks/useVenueVibes';
import { auth } from '../firebase';
import { useI18n } from '../i18n';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizerProfile'>;
type TabKey = 'upcoming' | 'past' | 'about';

const { width } = Dimensions.get('window');
const GRID_GAP = 3;
const GRID_HORIZONTAL_PADDING = 0;
const POST_SIZE = Math.floor((width - GRID_HORIZONTAL_PADDING * 2 - GRID_GAP * 2) / 3);
const COVER_HEIGHT = Math.min(260, Math.max(210, width * 0.58));

const toImageSource = (value: number | string | null) => {
  if (!value) return null;
  return typeof value === 'number' ? value : { uri: value };
};

function EventPostTile({ event, onPress, onLongPress }: { event: TonyteActivity; onPress: () => void; onLongPress: () => void }) {
  const [image, setImage] = useState<number | string | null>(null);
  const stats = useVenuePostStats(getResolvedEventId(event), null, false);
  useEffect(() => {
    let active = true;
    resolveActivityImage(event).then(resolved => {
      if (active) setImage(resolved.source);
    }).catch(() => {
      if (active) setImage(null);
    });
    return () => { active = false; };
  }, [event]);

  const source = toImageSource(image);
  return (
    <TouchableOpacity activeOpacity={0.88} onPress={onPress} onLongPress={onLongPress} delayLongPress={260} style={styles.postTile}>
      {source ? <Image source={source} style={StyleSheet.absoluteFill} resizeMode="cover" resizeMethod="resize" /> : <LinearGradient colors={['#35136A', '#111827']} style={StyleSheet.absoluteFill} />}
      <LinearGradient colors={['rgba(0,0,0,0.03)', 'rgba(0,0,0,0.68)']} style={StyleSheet.absoluteFill} />
      <View style={styles.collabPill}>
        <Icon name="users" size={10} color="#fff" />
        <Text style={styles.collabText} numberOfLines={1}>{getCollabLabel(event)}</Text>
      </View>
      <Text style={styles.postTitle} numberOfLines={2}>{event.title}</Text>
      <View style={styles.postStats}>
        <Icon name="heart" size={10} color="#fff" />
        <Text style={styles.postStatsText}>{stats.likesCount}</Text>
        <Icon name="message-circle" size={10} color="#fff" />
        <Text style={styles.postStatsText}>{stats.commentsCount}</Text>
      </View>
    </TouchableOpacity>
  );
}

function PostPreviewModal({
  event,
  visible,
  onClose,
  onOpenEvent,
}: {
  event: TonyteActivity | null;
  visible: boolean;
  onClose: () => void;
  onOpenEvent: (event: TonyteActivity) => void;
}) {
  const userId = auth.currentUser?.uid || null;
  const [image, setImage] = useState<number | string | null>(null);
  const interactions = useVenuePostInteractions(event, userId);

  useEffect(() => {
    if (!event) return;
    let active = true;
    resolveActivityImage(event).then(resolved => {
      if (active) setImage(resolved.source);
    }).catch(() => {
      if (active) setImage(null);
    });
    return () => { active = false; };
  }, [event]);

  if (!event) return null;
  const source = toImageSource(image);

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <Pressable style={styles.previewBackdrop} onPress={onClose}>
        <Pressable style={styles.previewCard} onPress={() => {}}>
          {source ? <Image source={source} style={styles.previewImage} resizeMode="cover" resizeMethod="resize" /> : <LinearGradient colors={['#35136A', '#111827']} style={styles.previewImage} />}
          <View style={styles.previewBody}>
            <View style={styles.previewHeader}>
              <View style={styles.previewTitleBlock}>
                <Text style={styles.previewTitle}>{event.title}</Text>
                <Text style={styles.previewMeta}>{getCollabLabel(event)}</Text>
              </View>
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <Icon name="x" size={18} color="#fff" />
              </TouchableOpacity>
            </View>
            <View style={styles.actionRow}>
              <TouchableOpacity disabled={interactions.pending} onPress={interactions.toggleLike} style={styles.actionButton}>
                <Icon name="heart" size={18} color={interactions.liked ? '#FF6BAA' : '#fff'} />
                <Text style={styles.actionText}>{interactions.likesCount}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onOpenEvent(event)} style={styles.actionButton}>
                <Icon name="message-circle" size={18} color="#fff" />
                <Text style={styles.actionText}>{interactions.commentsCount}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={interactions.sharePost} style={styles.actionButton}>
                <Icon name="send" size={18} color="#fff" />
                <Text style={styles.actionText}>{interactions.sharesCount}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const OrganizerProfileScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const userId = auth.currentUser?.uid || null;
  const { organizerId, organizer: passedOrganizer } = route.params;
  const { events, loading } = useVenueVibes();
  const organizerEvents = useMemo(() => filterOrganizerEvents(events, organizerId), [events, organizerId]);
  const derivedOrganizer = useMemo(
    () => buildOrganizerSummaries(organizerEvents).find(item => item.id === organizerId) || passedOrganizer || null,
    [organizerEvents, organizerId, passedOrganizer],
  );
  const { following, followersCount, pending, toggleFollow } = useOrganizerFollow(derivedOrganizer, userId);
  const [tab, setTab] = useState<TabKey>('upcoming');
  const [previewPost, setPreviewPost] = useState<TonyteActivity | null>(null);
  const split = useMemo(() => splitVenueEvents(organizerEvents), [organizerEvents]);

  if (loading && !derivedOrganizer) return <LoadingSpinner />;
  if (!derivedOrganizer) {
    Alert.alert(t('venueProfiles.organizerNotFound'), t('venueProfiles.organizerUnavailable'));
    navigation.goBack();
    return null;
  }

  const posts = tab === 'upcoming' ? split.upcoming : tab === 'past' ? split.past : [];
  const coverSource = derivedOrganizer.coverImage ? { uri: derivedOrganizer.coverImage } : null;
  const avatarSource = derivedOrganizer.avatarUrl ? { uri: derivedOrganizer.avatarUrl } : null;

  const openEvent = (event: TonyteActivity) => {
    setPreviewPost(null);
    trackEventDetailOpen({ eventId: getResolvedEventId(event), organizerId: event.hostId, source: ANALYTICS_SOURCES.PARTNER_PROFILE });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(event), event });
  };

  return (
    <TonyteBackground>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <AmbientGlow tone="teal" size={240} intensity={0.18} style={styles.topGlow} />
      <FlatList
        data={posts}
        keyExtractor={item => getResolvedEventId(item)}
        numColumns={3}
        columnWrapperStyle={styles.postRow}
        ListHeaderComponent={
          <View>
            <View style={styles.cover}>
              {coverSource ? <Image source={coverSource} style={StyleSheet.absoluteFill} resizeMode="cover" resizeMethod="resize" /> : <LinearGradient colors={['#271052', '#090B14']} style={StyleSheet.absoluteFill} />}
              <LinearGradient colors={['rgba(3,5,12,0.1)', 'rgba(3,5,12,0.82)']} style={StyleSheet.absoluteFill} />
              <View style={[styles.coverNav, { paddingTop: insets.top + 10 }]}>
                <TouchableOpacity style={styles.iconButton} onPress={() => navigation.goBack()}>
                  <Icon name="chevron-left" size={22} color="#fff" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.iconButton} onPress={() => shareOrganizerProfile(derivedOrganizer)}>
                  <Icon name="send" size={18} color="#fff" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.profileBlock}>
              <View style={styles.logoWrap}>
                {avatarSource ? <Image source={avatarSource} style={styles.logoImage} /> : <Text style={styles.logoLetter}>{derivedOrganizer.displayName.slice(0, 1).toUpperCase()}</Text>}
              </View>
              <View style={styles.nameRow}>
                <Text style={styles.profileName}>{derivedOrganizer.displayName}</Text>
                {derivedOrganizer.verified ? <Icon name="check-circle" size={18} color={Colors.turquoise} /> : null}
              </View>
              <Text style={styles.username}>@{derivedOrganizer.username}</Text>
              <Text style={styles.meta}>{derivedOrganizer.category} · {derivedOrganizer.city}</Text>
              <View style={styles.statsRow}>
                <View style={styles.stat}><Text style={styles.statNumber}>{followersCount}</Text><Text style={styles.statLabel}>{t('venueProfiles.followers')}</Text></View>
                <View style={styles.stat}><Text style={styles.statNumber}>{derivedOrganizer.eventsCount}</Text><Text style={styles.statLabel}>{t('venueProfiles.events')}</Text></View>
                <View style={styles.stat}><Text style={styles.statNumber}>{derivedOrganizer.upcomingCount}</Text><Text style={styles.statLabel}>{t('venueProfiles.upcoming')}</Text></View>
              </View>
              <Text style={styles.bio}>{derivedOrganizer.bio}</Text>
              <View style={styles.buttonRow}>
                <TouchableOpacity disabled={pending} onPress={toggleFollow} style={[styles.followButton, following && styles.followingButton]}>
                  <Text style={styles.followText}>{following ? t('venueProfiles.following') : t('venueProfiles.follow')}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => shareOrganizerProfile(derivedOrganizer)} style={styles.profileIconButton}>
                  <Icon name="send" size={17} color="#fff" />
                </TouchableOpacity>
              </View>
              <View style={styles.tabs}>
                {(['upcoming', 'past', 'about'] as TabKey[]).map(item => (
                  <TouchableOpacity key={item} onPress={() => setTab(item)} style={[styles.tabButton, tab === item && styles.tabButtonActive]}>
                    <Text style={[styles.tabText, tab === item && styles.tabTextActive]}>{item === 'upcoming' ? t('venueProfiles.upcoming') : item === 'past' ? t('venueProfiles.past') : t('venueProfiles.about')}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {tab === 'about' ? (
                <View style={styles.about}>
                  <Text style={styles.aboutTitle}>{t('venueProfiles.about')}</Text>
                  <Text style={styles.aboutText}>{derivedOrganizer.bio}</Text>
                  <View style={styles.tagWrap}>
                    {derivedOrganizer.tags.map(tag => <Text key={tag} style={styles.tag}>#{tag.replace(/^#/, '')}</Text>)}
                  </View>
                </View>
              ) : null}
            </View>
          </View>
        }
        renderItem={({ item }) => (
          <EventPostTile event={item} onPress={() => openEvent(item)} onLongPress={() => setPreviewPost(item)} />
        )}
        ListEmptyComponent={tab === 'about' ? null : (
          <View style={styles.empty}>
            <Icon name="calendar" size={24} color={Colors.purple} />
            <Text style={styles.emptyText}>{tab === 'upcoming' ? 'No upcoming organizer posts yet.' : 'No past organizer posts yet.'}</Text>
          </View>
        )}
        ListFooterComponent={<View style={{ height: insets.bottom + 30 }} />}
        showsVerticalScrollIndicator={false}
      />
      <PostPreviewModal
        event={previewPost}
        visible={Boolean(previewPost)}
        onClose={() => setPreviewPost(null)}
        onOpenEvent={openEvent}
      />
    </TonyteBackground>
  );
};

const styles = StyleSheet.create({
  topGlow: { top: -130, right: -100 },
  cover: { height: COVER_HEIGHT, overflow: 'hidden', backgroundColor: '#080A13' },
  coverNav: { paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  iconButton: { width: 39, height: 39, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.42)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)' },
  profileBlock: { paddingHorizontal: 18, paddingBottom: 16, marginTop: -42 },
  logoWrap: { width: 88, height: 88, borderRadius: 44, alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827', borderWidth: 3, borderColor: '#080A13', overflow: 'hidden' },
  logoImage: { width: '100%', height: '100%' },
  logoLetter: { color: '#fff', fontSize: 34, fontWeight: Typography.extrabold },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 },
  profileName: { color: '#fff', fontSize: 25, lineHeight: 30, fontWeight: Typography.extrabold, flexShrink: 1 },
  username: { color: 'rgba(255,255,255,0.62)', fontSize: 14, marginTop: 2 },
  meta: { color: '#D8B4FE', fontSize: 13, fontWeight: Typography.bold, marginTop: 10 },
  statsRow: { marginTop: 17, flexDirection: 'row' },
  stat: { flex: 1, alignItems: 'center', paddingVertical: 12 },
  statNumber: { color: '#fff', fontSize: 18, fontWeight: Typography.extrabold },
  statLabel: { color: 'rgba(255,255,255,0.54)', fontSize: 11, marginTop: 2 },
  bio: { color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 20, marginTop: 15 },
  buttonRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 16 },
  followButton: { flex: 1, height: 44, borderRadius: Radius.pill, alignItems: 'center', justifyContent: 'center', backgroundColor: '#8B5CF6' },
  followingButton: { backgroundColor: 'rgba(255,255,255,0.12)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)' },
  followText: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },
  profileIconButton: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.10)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)' },
  tabs: { flexDirection: 'row', marginTop: 18, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.10)' },
  tabButton: { flex: 1, alignItems: 'center', paddingVertical: 13, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabButtonActive: { borderBottomColor: '#D8B4FE' },
  tabText: { color: 'rgba(255,255,255,0.52)', fontSize: 13, fontWeight: Typography.bold },
  tabTextActive: { color: '#fff' },
  postRow: { gap: GRID_GAP },
  postTile: { width: POST_SIZE, height: POST_SIZE, marginBottom: GRID_GAP, overflow: 'hidden', backgroundColor: '#111827' },
  collabPill: { position: 'absolute', left: 7, right: 7, top: 7, minHeight: 20, borderRadius: 10, paddingHorizontal: 6, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.42)' },
  collabText: { flex: 1, color: '#fff', fontSize: 9, fontWeight: Typography.extrabold },
  postTitle: { position: 'absolute', left: 7, right: 7, bottom: 25, color: '#fff', fontSize: 11, lineHeight: 14, fontWeight: Typography.extrabold },
  postStats: { position: 'absolute', left: 7, bottom: 7, flexDirection: 'row', alignItems: 'center', gap: 4 },
  postStatsText: { color: '#fff', fontSize: 10, fontWeight: Typography.bold, marginRight: 4 },
  empty: { paddingVertical: 60, alignItems: 'center' },
  emptyText: { color: 'rgba(255,255,255,0.62)', marginTop: 10 },
  about: { paddingVertical: 18 },
  aboutTitle: { color: '#fff', fontSize: 17, fontWeight: Typography.extrabold, marginBottom: 8 },
  aboutText: { color: 'rgba(255,255,255,0.72)', fontSize: 14, lineHeight: 21 },
  tagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  tag: { color: '#D8B4FE', fontSize: 12, fontWeight: Typography.bold, paddingHorizontal: 10, paddingVertical: 6, borderRadius: Radius.pill, backgroundColor: 'rgba(168,85,247,0.14)' },
  previewBackdrop: { flex: 1, justifyContent: 'center', paddingHorizontal: 18, backgroundColor: 'rgba(0,0,0,0.66)' },
  previewCard: { borderRadius: 26, backgroundColor: '#080A13', overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(216,180,254,0.24)' },
  previewImage: { width: '100%', height: Math.min(300, width * 0.72), backgroundColor: '#111827' },
  previewBody: { padding: 16, paddingBottom: 22 },
  previewHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  previewTitleBlock: { flex: 1, minWidth: 0 },
  previewTitle: { color: '#fff', fontSize: 20, fontWeight: Typography.extrabold, lineHeight: 25 },
  previewMeta: { color: 'rgba(255,255,255,0.58)', fontSize: 13, marginTop: 3 },
  closeButton: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.08)' },
  actionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 16 },
  actionButton: { height: 38, minWidth: 58, borderRadius: 19, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.09)' },
  actionText: { color: '#fff', fontSize: 12, fontWeight: Typography.bold },
});

export default OrganizerProfileScreen;
